// IndexedDB helper for offline data storage
const DB_NAME = 'voilaw-offline';
const DB_VERSION = 2;
const STORES = {
    PENDING_ENTRIES: 'pending-time-entries',
    CACHED_CASES: 'cached-cases',
    FULL_BACKUP: 'full-backup'
};

let db = null;

// Initialize the database
export const initDB = () => {
    return new Promise((resolve, reject) => {
        if (db) {
            resolve(db);
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('Failed to open IndexedDB:', request.error);
            reject(request.error);
        };

        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const database = event.target.result;

            // Store for pending time entries
            if (!database.objectStoreNames.contains(STORES.PENDING_ENTRIES)) {
                const entryStore = database.createObjectStore(STORES.PENDING_ENTRIES, {
                    keyPath: 'localId',
                    autoIncrement: true
                });
                entryStore.createIndex('caseId', 'case_id', { unique: false });
                entryStore.createIndex('createdAt', 'created_at', { unique: false });
            }

            // Store for cached cases (for offline case selection)
            if (!database.objectStoreNames.contains(STORES.CACHED_CASES)) {
                database.createObjectStore(STORES.CACHED_CASES, {
                    keyPath: 'id'
                });
            }

            // Store for full data backup
            if (!database.objectStoreNames.contains(STORES.FULL_BACKUP)) {
                database.createObjectStore(STORES.FULL_BACKUP, {
                    keyPath: 'key'
                });
            }
        };
    });
};

// Generic transaction helper
const withStore = async (storeName, mode, callback) => {
    const database = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = database.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        
        transaction.onerror = () => reject(transaction.error);
        transaction.oncomplete = () => resolve();
        
        callback(store, resolve, reject);
    });
};

// ============ Pending Time Entries ============

// Add a pending time entry
export const addPendingEntry = async (entry) => {
    const entryWithMeta = {
        ...entry,
        created_at: new Date().toISOString(),
        sync_status: 'pending'
    };

    return withStore(STORES.PENDING_ENTRIES, 'readwrite', (store, resolve) => {
        const request = store.add(entryWithMeta);
        request.onsuccess = () => resolve(request.result);
    });
};

// Get all pending entries
export const getPendingEntries = async () => {
    return withStore(STORES.PENDING_ENTRIES, 'readonly', (store, resolve) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
    });
};

// Get pending entries for a specific case
export const getPendingEntriesForCase = async (caseId) => {
    return withStore(STORES.PENDING_ENTRIES, 'readonly', (store, resolve) => {
        const index = store.index('caseId');
        const request = index.getAll(caseId);
        request.onsuccess = () => resolve(request.result);
    });
};

// Remove a pending entry (after successful sync)
export const removePendingEntry = async (localId) => {
    return withStore(STORES.PENDING_ENTRIES, 'readwrite', (store, resolve) => {
        store.delete(localId);
        resolve();
    });
};

// Clear all pending entries
export const clearPendingEntries = async () => {
    return withStore(STORES.PENDING_ENTRIES, 'readwrite', (store, resolve) => {
        store.clear();
        resolve();
    });
};

// Update entry sync status
export const updateEntryStatus = async (localId, status) => {
    return withStore(STORES.PENDING_ENTRIES, 'readwrite', (store, resolve) => {
        const request = store.get(localId);
        request.onsuccess = () => {
            const entry = request.result;
            if (entry) {
                entry.sync_status = status;
                store.put(entry);
            }
            resolve();
        };
    });
};

// ============ Cached Cases ============

// Cache cases for offline use
export const cacheCases = async (cases) => {
    return withStore(STORES.CACHED_CASES, 'readwrite', (store, resolve) => {
        store.clear();
        cases.forEach(caseItem => store.put(caseItem));
        resolve();
    });
};

// Get cached cases
export const getCachedCases = async () => {
    return withStore(STORES.CACHED_CASES, 'readonly', (store, resolve) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
    });
};

// Get pending entry count
export const getPendingCount = async () => {
    const entries = await getPendingEntries();
    return entries.length;
};

// ============ Full Data Backup ============

// Save full backup to IndexedDB
export const saveFullBackup = async (data) => {
    const backupData = {
        key: 'full-backup',
        cases: data.cases || [],
        timeEntries: data.timeEntries || [],
        reminders: data.reminders || [],
        userSettings: data.userSettings || {},
        backupDate: new Date().toISOString(),
        version: '1.0'
    };

    return withStore(STORES.FULL_BACKUP, 'readwrite', (store, resolve) => {
        const request = store.put(backupData);
        request.onsuccess = () => resolve(backupData);
    });
};

// Get full backup from IndexedDB
export const getFullBackup = async () => {
    return withStore(STORES.FULL_BACKUP, 'readonly', (store, resolve) => {
        const request = store.get('full-backup');
        request.onsuccess = () => resolve(request.result || null);
    });
};

// Clear full backup
export const clearFullBackup = async () => {
    return withStore(STORES.FULL_BACKUP, 'readwrite', (store, resolve) => {
        store.clear();
        resolve();
    });
};

// Get backup info (date, size)
export const getBackupInfo = async () => {
    const backup = await getFullBackup();
    if (!backup) return null;
    
    return {
        backupDate: backup.backupDate,
        casesCount: backup.cases?.length || 0,
        entriesCount: backup.timeEntries?.length || 0,
        remindersCount: backup.reminders?.length || 0
    };
};

export default {
    initDB,
    addPendingEntry,
    getPendingEntries,
    getPendingEntriesForCase,
    removePendingEntry,
    clearPendingEntries,
    updateEntryStatus,
    cacheCases,
    getCachedCases,
    getPendingCount,
    saveFullBackup,
    getFullBackup,
    clearFullBackup,
    getBackupInfo
};
